import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icons';
import type { ApprovalState, TaskPriority, TaskStatus } from '@/domain/types';
import { priorityLabel, taskStatusLabel } from '@/services/task-service';

/* --------------------------------- الأزرار -------------------------------- */

type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'danger' | 'soft';

export function Button({
  variant = 'outline',
  size,
  loading,
  icon,
  iconEnd,
  children,
  className,
  disabled,
  ...rest
}: {
  variant?: ButtonVariant;
  size?: 'sm';
  loading?: boolean;
  icon?: IconName;
  iconEnd?: IconName;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = [
    'btn',
    variant === 'primary' ? 'btn-primary' : `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : null}
      {icon && !loading ? <Icon name={icon} size={size === 'sm' ? 15 : 17} /> : null}
      {children}
      {iconEnd ? <Icon name={iconEnd} size={size === 'sm' ? 15 : 17} /> : null}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  ...rest
}: { icon: IconName; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className="btn btn-icon" aria-label={label} title={label} {...rest}>
      <Icon name={icon} />
    </button>
  );
}

/* --------------------------------- الحقول --------------------------------- */

export function Field({
  label,
  required,
  error,
  hint,
  htmlFor,
  children,
  className,
}: {
  label?: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`field ${className ?? ''}`}>
      {label ? (
        <label className="label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="req">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <span className="error-text">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function TextInput({
  label,
  error,
  hint,
  required,
  className,
  containerClassName,
  type,
  value,
  onChange,
  placeholder,
  ...rest
}: { label?: string; error?: string | null; hint?: string; containerClassName?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const autoId = useId();
  const id = rest.id ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);

  if (type === 'date') {
    const stringVal = typeof value === 'string' ? value : (value ? String(value) : '');
    const displayPlaceholder = placeholder || 'اختر التاريخ';
    return (
      <Field label={label} required={required} error={error} hint={hint} htmlFor={id} className={containerClassName}>
        <div
          className={`date-input-box ${stringVal ? 'has-value' : ''} ${error ? 'has-error' : ''} ${className ?? ''}`}
          onClick={() => {
            try {
              if (inputRef.current && 'showPicker' in inputRef.current && typeof (inputRef.current as any).showPicker === 'function') {
                (inputRef.current as any).showPicker();
              } else {
                inputRef.current?.focus();
              }
            } catch {
              inputRef.current?.focus();
            }
          }}
        >
          <span className="date-input-icon" aria-hidden="true">
            <Icon name="calendar" size={16} />
          </span>
          <span className={`date-input-text ${!stringVal ? 'is-placeholder' : ''}`}>
            {stringVal || displayPlaceholder}
          </span>
          {stringVal && onChange ? (
            <button
              type="button"
              className="date-input-clear"
              aria-label="مسح التاريخ"
              title="مسح التاريخ"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const syntheticEvent = {
                  target: { value: '' },
                  currentTarget: { value: '' },
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                onChange(syntheticEvent);
              }}
            >
              <Icon name="close" size={13} />
            </button>
          ) : null}
          <input
            ref={inputRef}
            id={id}
            type="date"
            className="date-input-native"
            value={stringVal}
            onChange={onChange}
            aria-label={label ?? displayPlaceholder}
            {...rest}
          />
        </div>
      </Field>
    );
  }

  return (
    <Field label={label} required={required} error={error} hint={hint} htmlFor={id} className={containerClassName}>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`input ${error ? 'has-error' : ''} ${className ?? ''}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  );
}

export function TextArea({
  label,
  error,
  hint,
  required,
  className,
  containerClassName,
  ...rest
}: { label?: string; error?: string | null; hint?: string; containerClassName?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Field label={label} required={required} error={error} hint={hint} htmlFor={rest.id ?? id} className={containerClassName}>
      <textarea
        id={rest.id ?? id}
        className={`textarea ${error ? 'has-error' : ''} ${className ?? ''}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  );
}

export function SelectInput({
  label,
  error,
  hint,
  required,
  className,
  containerClassName,
  children,
  ...rest
}: { label?: string; error?: string | null; hint?: string; containerClassName?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <Field label={label} required={required} error={error} hint={hint} htmlFor={rest.id ?? id} className={containerClassName}>
      <select
        id={rest.id ?? id}
        className={`select ${error ? 'has-error' : ''} ${className ?? ''}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {children}
      </select>
    </Field>
  );
}

export function Checkbox({
  label,
  hint,
  ...rest
}: { label: ReactNode; hint?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>
        <span>{label}</span>
        {hint ? <span className="hint" style={{ display: 'block' }}>{hint}</span> : null}
      </span>
    </label>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'بحث…',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="search-box">
      <span className="search-icon">
        <Icon name="search" size={17} />
      </span>
      <input
        className="input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

/* --------------------------------- البطاقات -------------------------------- */

export function Card({
  children,
  className,
  style,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}) {
  return (
    <div
      className={`card ${onClick ? 'clickable' : ''} ${className ?? ''}`}
      style={style}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="card-header">
      <div>
        <h2>{title}</h2>
        {subtitle ? <div className="tiny muted">{subtitle}</div> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

/* --------------------------------- الشارات -------------------------------- */

export function Badge({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'muted';
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function StatusBadge({ status }: { status: TaskStatus }) {
  const tone =
    status === 'completed'
      ? 'success'
      : status === 'cancelled'
        ? 'muted'
        : status === 'in_progress'
          ? 'info'
          : status === 'scheduled'
            ? 'primary'
            : 'warning';
  return <Badge tone={tone}>{taskStatusLabel(status)}</Badge>;
}

export function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const tone =
    priority === 'urgent' ? 'danger' : priority === 'high' ? 'warning' : priority === 'low' ? 'muted' : 'info';
  return <Badge tone={tone}>{priorityLabel(priority)}</Badge>;
}

export function ApprovalBadge({ state }: { state: ApprovalState }) {
  if (state === 'not_required') return null;
  const labels: Record<ApprovalState, string> = {
    not_required: '',
    pending: 'بانتظار الاعتماد',
    approved: 'معتمدة',
    returned: 'معادة',
  };
  const tone = state === 'approved' ? 'success' : state === 'returned' ? 'danger' : 'warning';
  return <Badge tone={tone}>{labels[state]}</Badge>;
}

/* --------------------------------- السبينر -------------------------------- */

export function Spinner({ large }: { large?: boolean }) {
  return <span className={`spinner ${large ? 'lg' : ''}`} role="status" aria-label="جارٍ التحميل" />;
}

export function LoadingBlock({
  label = 'جارٍ التحميل…',
  minHeight = 120,
}: {
  label?: string;
  minHeight?: number;
}) {
  return (
    <div className="loading-block pe-fade-in" style={{ minHeight }}>
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

/* --------------------------------- الحالة الفارغة ---------------------------- */

export function EmptyState({
  icon = 'inbox',
  title,
  description,
  action,
}: {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon name={icon} size={24} />
      </div>
      <h3>{title}</h3>
      {description ? <p className="small" style={{ maxWidth: 420 }}>{description}</p> : null}
      {action}
    </div>
  );
}

/* --------------------------------- النوافذ --------------------------------- */

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.activeElement as HTMLElement | null;
    const timer = window.setTimeout(() => {
      const focusable =
        ref.current?.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
        ) ??
        ref.current?.querySelector<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        );
      focusable?.focus();
    }, 30);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      window.clearTimeout(timer);
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="modal-header">
          <h2>{title}</h2>
          <IconButton icon="close" label="إغلاق" onClick={onClose} type="button" />
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'تأكيد',
  danger,
  loading,
  confirmDisabled,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="button">
            إلغاء
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading} disabled={confirmDisabled} type="button">
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="small" style={{ color: 'var(--text-2)' }}>{message}</div>
    </Modal>
  );
}

/* --------------------------------- التبويبات ------------------------------- */

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ key: T; label: string; count?: number }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === value}
          className={`tab ${tab.key === value ? 'active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count !== undefined ? <span className="muted"> ({tab.count})</span> : null}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------- الترقيم --------------------------------- */

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number | null;
  onPage: (page: number) => void;
}) {
  const pages = total !== null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  return (
    <div className="pagination">
      <span>
        صفحة {page}
        {pages ? ` من ${pages}` : ''}
        {total !== null ? ` — ${total} سجل` : ''}
      </span>
      <div className="row">
        <Button
          size="sm"
          icon="chevron-right"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          type="button"
        >
          السابق
        </Button>
        <Button
          size="sm"
          iconEnd="chevron-left"
          onClick={() => onPage(page + 1)}
          disabled={pages !== null && page >= pages}
          type="button"
        >
          التالي
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------- الصورة الرمزية ---------------------------- */

export function Avatar({
  name,
  src,
  large,
  size,
}: {
  name: string;
  src?: string | null;
  large?: boolean;
  size?: number;
}) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('');
  const sizeStyle = size ? { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)) } : undefined;
  if (src) {
    return (
      <span className={`avatar ${large ? 'lg' : ''}`} style={sizeStyle} aria-hidden="true">
        <img src={src} alt="" className="avatar-img" />
      </span>
    );
  }
  return (
    <span className={`avatar ${large ? 'lg' : ''}`} style={sizeStyle} aria-hidden="true">
      {initials || '؟'}
    </span>
  );
}

/* --------------------------------- المفتاح --------------------------------- */

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  showLabel = true,
  size = 'md',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md';
}) {
  return (
    <span className="switch-wrap">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        title={label}
        disabled={disabled}
        className={`switch${size === 'sm' ? ' sm' : ''}${checked ? ' on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onChange(!checked);
        }}
      >
        <span className="switch-thumb" aria-hidden="true" />
      </button>
      {showLabel ? <span className="small">{label}</span> : null}
    </span>
  );
}

export function SwitchField({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="switch-field"
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <Switch
        checked={checked}
        onChange={onChange}
        label={label}
        disabled={disabled}
        showLabel={false}
      />
      <div className="switch-field-text">
        <div className="switch-field-label">{label}</div>
        {hint ? <div className="hint">{hint}</div> : null}
      </div>
    </div>
  );
}

/* --------------------------------- أدوات مساعدة ----------------------------- */

export function useCloseOnEscape(open: boolean, close: () => void): void {
  const stable = useCallback(() => close(), [close]);
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') stable();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, stable]);
}

export function ErrorAlert({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: 'var(--danger-soft)',
        color: 'var(--danger)',
        border: '1px solid var(--danger)',
        borderRadius: 'var(--radius-sm)',
        padding: '10px 12px',
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {message}
    </div>
  );
}

export { Skeleton, DashboardSkeleton, TableSkeleton, CardsGridSkeleton } from './Skeleton';

